import { useCallback, useContext, useMemo } from 'react'
import moment from 'moment'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import GeoContext from 'contexts/geoContext'
import TripDrawer from 'tripPlanner/components/GenericDrawer/TripDrawer'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import FilterChip from 'LuxKit/Chips/FilterChip'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import HotelDetailView from './HotelListingDrawer/HotelDetailView'
import type { DrawerHotel } from './HotelListingDrawer'
import { mapOfferDetailToDrawerHotel } from './HotelListingDrawer/mapOfferDetailToDrawerHotel'
import useOfferDetails from '../hooks/useOfferDetails'
import useOfferReviews from '../hooks/useOfferReviews'
import type { TripItineraryData } from 'tripPlanner/api/types'
import type { OfferClickSearchParams } from 'tripPlanner/pages/chat/ChatTripPlannerPage/contexts/ChatMessageActionsContext'
import { isOfferBookedInItinerary, getOfferItineraryDayRange, getOfferOrderId } from 'tripPlanner/utils/chatItinerary'
import { DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { prettyFlexibleDates } from 'components/Search/SearchForm/SearchDateInput/FlexibleDateSearchField'

const NonInteractiveChipWrapper = styled.div`
  pointer-events: none;
`

const SkeletonWrapper = styled.div`
  padding: ${rem(20)};
`

const FullWidthLoadingBox = styled(LoadingBox)`
  width: 100%;
`

const NarrowLoadingBox = styled(LoadingBox)`
  width: 80%;
`

const ErrorWrapper = styled(Group)`
  padding: ${rem(48)} ${rem(24)};
  text-align: center;
`

interface Props {
  offerId: string
  accommodationPropertyId?: string
  offerType?: string
  isOpen: boolean
  onClose: () => void
  onAfterClose?: () => void
  onShowMap?: (hotel: DrawerHotel) => void
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string) => void
  searchParams?: OfferClickSearchParams
}

function ChatOfferDetailDrawer({
  offerId,
  accommodationPropertyId,
  offerType,
  isOpen,
  onClose,
  onAfterClose,
  onShowMap,
  itineraryData,
  onSendMessage,
  searchParams,
}: Props) {
  const intl = useIntl()
  const geo = useContext(GeoContext)

  const { offerDetail, isLoading, isError, refetch } = useOfferDetails({
    offerId: isOpen ? offerId : undefined,
    region: geo.currentRegionCode,
    offerType,
    accommodationPropertyId,
    checkIn: searchParams?.checkIn,
    checkOut: searchParams?.checkOut,
    occupancies: searchParams?.rooms
      ? JSON.stringify(searchParams.rooms.map(r => ({
          adults: r.adults,
          childrenAge: r.childrenAge ?? [],
        })))
      : undefined,
    flexibleMonths: searchParams?.flexibleMonths,
    flexibleNights: searchParams?.flexibleNights,
    enabled: isOpen,
  })

  const { reviews, isLoading: reviewsLoading } = useOfferReviews({
    offerId: isOpen ? offerId : undefined,
    region: geo.currentRegionCode,
    enabled: isOpen && !!offerDetail,
  })

  const drawerHotel = useMemo(() => {
    if (!offerDetail) return undefined
    const hotel = mapOfferDetailToDrawerHotel(offerDetail)

    // Adjust display price if dates override default duration
    if (searchParams?.checkIn && searchParams?.checkOut) {
      const nights = moment(searchParams.checkOut).diff(moment(searchParams.checkIn), 'days')
      if (nights > 0 && hotel.price) {
        return { ...hotel, price: { ...hotel.price, nights } }
      }
    }

    return hotel
  }, [offerDetail, searchParams])

  const handleShowMap = useCallback(() => {
    if (drawerHotel) {
      onClose()
      onShowMap?.(drawerHotel)
    }
  }, [onClose, onShowMap, drawerHotel])

  const datesSummary = useMemo(() => {
    if (!searchParams) return undefined
    if (searchParams.checkIn && searchParams.checkOut) {
      const checkIn = moment(searchParams.checkIn)
      const checkOut = moment(searchParams.checkOut)
      const nights = checkOut.diff(checkIn, 'days')
      return intl.formatMessage(
        {
          id: 'Hru7EDZPgAoClyps-pbR',
          defaultMessage: '{checkIn} – {checkOut} ({nights, plural, one {# night} other {# nights}})',
          description: 'Date range summary with number of nights in hotel detail drawer',
        },
        {
          checkIn: checkIn.format(DMY_CASUAL_SHORT_FORMAT),
          checkOut: checkOut.format(DMY_CASUAL_SHORT_FORMAT),
          nights,
        },
      )
    }
    if (searchParams.flexibleMonths || searchParams.flexibleNights) {
      return prettyFlexibleDates(
        searchParams.flexibleMonths,
        searchParams.flexibleNights,
      )
    }
    return undefined
  }, [searchParams, intl])

  const hasGeoLocation = !!drawerHotel?.geoLocation

  const itineraryDayLabel = useMemo(() => {
    const range = getOfferItineraryDayRange(offerId, itineraryData)
    return range?.label
  }, [offerId, itineraryData])

  const dateLabel = datesSummary ?? itineraryDayLabel

  const orderId = useMemo(() => getOfferOrderId(offerId, itineraryData), [offerId, itineraryData])

  const headerActions = useMemo(() => {
    if (!drawerHotel) return undefined
    if (!hasGeoLocation && !dateLabel) return undefined
    const showMapLabel = intl.formatMessage({
      id: 'uyDWABuVrlw97fTIbbjL',
      defaultMessage: 'Show map',
      description: 'Button label to show hotel location on map in hotel detail view',
    })
    return (
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {hasGeoLocation && (
          <FilterChip size="small" startIcon={<LineMapIcon />} onClick={handleShowMap}>
            {showMapLabel}
          </FilterChip>
        )}
        {dateLabel && (
          <NonInteractiveChipWrapper>
            <FilterChip size="small">
              {dateLabel}
            </FilterChip>
          </NonInteractiveChipWrapper>
        )}
      </Group>
    )
  }, [drawerHotel, hasGeoLocation, handleShowMap, dateLabel, intl])

  const failedToLoadLabel = intl.formatMessage({
    id: 'AyMdUB-fKPPyurSXTes2',
    defaultMessage: 'Failed to load hotel details.',
    description: 'Error message shown when hotel offer details fail to load in the drawer',
  })

  const retryLabel = intl.formatMessage({
    id: 'q2nIV5-Sco41UZtxNQNg',
    defaultMessage: 'Try again',
    description: 'Button label to retry loading hotel offer details after an error',
  })

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={onClose}
      onAfterClose={onAfterClose}
      direction="right-to-left"
      headerActions={headerActions}
    >
      {isLoading && (
        <SkeletonWrapper>
          <Group direction="vertical" gap={16}>
            <FullWidthLoadingBox height={200} borderRadius="M" />
            <LoadingBox height={28} width={240} borderRadius="S" />
            <LoadingBox height={20} width={160} borderRadius="S" />
            <FullWidthLoadingBox height={16} borderRadius="S" />
            <NarrowLoadingBox height={16} borderRadius="S" />
          </Group>
        </SkeletonWrapper>
      )}
      {isError && !isLoading && !drawerHotel && (
        <ErrorWrapper direction="vertical" gap={8} horizontalAlign="center" verticalAlign="center">
          <BodyText variant="medium" colour="critical">
            {failedToLoadLabel}
          </BodyText>
          <TextButton kind="secondary" size="small" onClick={() => refetch()}>
            {retryLabel}
          </TextButton>
        </ErrorWrapper>
      )}
      {drawerHotel && (
        <HotelDetailView
          hotel={drawerHotel}
          itineraryData={itineraryData}
          onSendMessage={onSendMessage}
          searchParams={searchParams}
          reviews={reviews}
          reviewsLoading={reviewsLoading}
          isBooked={isOfferBookedInItinerary(offerId, itineraryData)}
          orderId={orderId}
        />
      )}
    </TripDrawer>
  )
}

export default ChatOfferDetailDrawer
